// AI Bot for Kosteczki

import { makeDecisionAsync } from './botUtils.js';
import { getAvailablePositions, getNeighborValues } from './botHelpers.js';
import { calculateScore } from './gameLogic.js';

/**
 * Simple AI bot for testing the game
 * Strategy:
 * 1. Places own marker if available
 * 2. If not, swaps with player who has most of bot's colors
 * 3. If cannot swap, places random marker avoiding helping opponents
 */

export class AIBot {
  constructor(playerIndex, colorIndex, difficulty = 'v1', rng = Math.random) {
    this.playerIndex = playerIndex;  // position in players array
    this.colorIndex = colorIndex;    // THIS player's scoring color (permanent)
    this.difficulty = difficulty;
    this.rng = rng;                  // RNG function; defaults to Math.random when no custom source is provided
  }

  /**
   * Main AI decision function
   * @param {Object} gameState - Current game state
   * @param {Array} gameState.players - List of players
   * @param {Array} gameState.gameBoard - Game board
   * @param {number} gameState.currentPlayer - Current player index
   * @param {Array} gameState.markersPool - Markers pool
   * @returns {Object} - AI Decision: { action: 'place' | 'swap', ... }
   */
  makeMove(gameState) {
    const { players, gameBoard, currentPlayer, markersPool } = gameState;
    const canSwap = gameState.canSwap !== false;
    const swapsAreLimited = typeof gameState.swapLimitPerPlayer === 'number' && gameState.swapLimitPerPlayer > 0;
    const myMarkers = players[currentPlayer].markers;

    // Build ordered list of candidate actions
    const actions = [];

    // Strategy 1: Do I have my own color?
    const myColorMarkerIndex = myMarkers.findIndex(m => m.colorIndex === this.colorIndex);
    if (myColorMarkerIndex !== -1) {
      actions.push(() => {
        const bestPosition = this.findBestPosition(
          gameBoard,
          this.colorIndex,
          'build'
        );
        return {
          action: 'place',
          markerIndex: myColorMarkerIndex,
          position: bestPosition
        };
      });
    }

    // Strategy 2: Swap with player who has most of my colors
    // Only swap if target has 2+ of my colors, or 70% chance with 1
    if (canSwap && markersPool.length > 0) {
      const bestSwapTarget = this.findBestSwapTarget(players, currentPlayer);
      if (bestSwapTarget !== -1) {
        const theirMyColors = players[bestSwapTarget].markers.filter(
          m => m.colorIndex === this.colorIndex
        ).length;
        const shouldSwap = swapsAreLimited
          ? theirMyColors >= 2
          : theirMyColors >= 2 || (theirMyColors === 1 && this.rng() < 0.7);
        if (shouldSwap) {
          actions.push(() => ({
            action: 'swap',
            targetPlayerId: bestSwapTarget
          }));
        }
      }
    }

    // Strategy 3: Place random marker avoiding helping opponents
    actions.push(() => {
      const randomMarkerIndex = Math.floor(this.rng() * myMarkers.length);
      const safePosition = this.findSafePosition(
        gameBoard,
        myMarkers[randomMarkerIndex].colorIndex
      );
      return {
        action: 'place',
        markerIndex: randomMarkerIndex,
        position: safePosition
      };
    });

    // Execute actions with 90/10 randomness: 90% chance to follow each action,
    // 10% chance to skip to the next one (adds unpredictability)
    for (let i = 0; i < actions.length; i++) {
      const isLastAction = i === actions.length - 1;
      if (isLastAction || this.rng() <= 0.9) {
        return actions[i]();
      }
    }

    // Fallback (should never reach here, but just in case)
    return actions[actions.length - 1]();
  }

  /**
   * Find best position to place marker (building own islands)
   * @param {Array} gameBoard - Current board state
   * @param {number} markerColorIndex - colorIndex of the marker being placed
   * @param {string} strategy - 'build' (only build strategy now)
   */
  findBestPosition(gameBoard, markerColorIndex, _strategy = 'build') {
    const availablePositions = this.getAvailablePositions(gameBoard);

    if (availablePositions.length === 0) {
      return null;
    }

    // Building strategy - find positions adjacent to my markers
    const scoredPositions = availablePositions.map(pos => {
      const score = this.evaluatePosition(gameBoard, pos, markerColorIndex, 'build');
      return { ...pos, score };
    });

    scoredPositions.sort((a, b) => b.score - a.score);
    return scoredPositions[0];
  }

  /**
   * Find safest position to place an opponent's marker (minimize help to ALL opponents)
   * Places the marker as far from any existing same-color island as possible.
   * @param {Array} gameBoard - Current board state
   * @param {number} markerColorIndex - colorIndex of the marker being placed (opponent's color)
   */
  findSafePosition(gameBoard, markerColorIndex) {
    const availablePositions = this.getAvailablePositions(gameBoard);

    if (availablePositions.length === 0) {
      return null;
    }

    // Score each position: penalize being near the marker's own color (opponent's islands)
    // and penalize being near ANY non-empty cell (to isolate the marker)
    const scoredPositions = availablePositions.map(pos => {
      const { row, col } = pos;
      let score = 0;
      const neighbors = this.getNeighbors(gameBoard, row, col);

      // Heavily penalize neighbors matching the marker's color (would grow opponent's island)
      const sameColorNeighbors = neighbors.filter(n => n === markerColorIndex).length;
      score -= sameColorNeighbors * 20;

      // Also penalize neighbors of any other player color (keep away from clusters)
      const anyColorNeighbors = neighbors.filter(n => typeof n === 'number').length;
      score -= anyColorNeighbors * 3;

      // Add randomness for unpredictability
      score += this.rng() * 3;

      return { ...pos, score };
    });

    scoredPositions.sort((a, b) => b.score - a.score);
    return scoredPositions[0];
  }

  /**
   * Evaluate position for building own islands (higher score = better).
   * BOT-2: uses the real island scoring delta instead of a fixed neighbour-count
   * multiplier.  Scoring formula (from gameLogic.calculateScore):
   *   island 1 → 0 pts | 2 → 1 | 3 → 4 | 4 → 7 | n≥4 → 7 + (n-4)*2
   * We simulate the placement and measure the actual score gain, then add
   * secondary heuristics as tiebreakers.
   *
   * @param {Array}  gameBoard        - Current board state
   * @param {Object} position         - { row, col }
   * @param {number} markerColorIndex - colorIndex of the marker being placed
   */
  evaluatePosition(gameBoard, position, markerColorIndex) {
    const { row, col } = position;

    // --- real delta score ---------------------------------------------------
    // Simulate placing marker (scoring ignores X/Y walls, only numeric values
    // matter, so we don't need to flip X→Y for this calculation).
    const simulatedBoard = gameBoard.map(r => [...r]);
    simulatedBoard[row][col] = markerColorIndex;
    const deltaScore = calculateScore(simulatedBoard, markerColorIndex)
                     - calculateScore(gameBoard, markerColorIndex);

    // --- tiebreaker heuristics ---------------------------------------------
    const neighbors = this.getNeighbors(gameBoard, row, col);
    const enemyNeighbors = neighbors.filter(n => typeof n === 'number' && n !== markerColorIndex).length;
    const wallNeighbors  = neighbors.filter(n => n === 'X').length;

    let score = deltaScore * 10;   // scale so heuristics stay secondary
    score -= enemyNeighbors * 2;   // slightly avoid opponents
    score += wallNeighbors  * 1.5; // X cells open up future moves
    score += this.rng()     * 4;   // unpredictability

    return score;
  }

  // Delegate to shared helpers from botHelpers.js (no duplication)
  getAvailablePositions(gameBoard) { return getAvailablePositions(gameBoard); }
  getNeighbors(gameBoard, row, col) { return getNeighborValues(gameBoard, row, col); }

  /**
   * Find best player to swap with (the one who has most of my colors)
   */
  findBestSwapTarget(players, currentPlayer) {
    let maxMyColors = 0;
    const candidateTargets = []; // Collect all candidates with max color count

    players.forEach((player, idx) => {
      if (idx === currentPlayer) return;

      // Count how many of my colors this player has (match by colorIndex, not player array position)
      const myColorsCount = player.markers.filter(
        m => m.colorIndex === this.colorIndex
      ).length;

      // If this player has more of my colors, clear candidate list
      if (myColorsCount > maxMyColors) {
        maxMyColors = myColorsCount;
        candidateTargets.length = 0; // Reset
        candidateTargets.push(idx);
      }
      // If it has the same as maximum, add to candidates
      else if (myColorsCount === maxMyColors && myColorsCount > 0) {
        candidateTargets.push(idx);
      }
    });

    // If we have candidates, pick a random one instead of always taking first
    if (candidateTargets.length > 0) {
      return candidateTargets[Math.floor(this.rng() * candidateTargets.length)];
    }

    // Swap only if player has at least 1 of my markers
    return -1;
  }
}

export const planAIMove = async (gameState, botProfile = null) => {
  if (!gameState || !gameState.players || gameState.currentPlayer === undefined) {
    return null;
  }

  const { players, currentPlayer } = gameState;
  const currentPlayerData = players[currentPlayer];

  if (!currentPlayerData || !currentPlayerData.isAI) {
    return null;
  }

  try {
    const profile = botProfile || {
      username: currentPlayerData.name,
      rating: currentPlayerData.rating || 1500,
      bot_persona: currentPlayerData.bot_persona || 'v1'
    };

    return await makeDecisionAsync(gameState, profile, currentPlayer, currentPlayerData.colorIndex);
  } catch (err) {
    console.error(`Bot decision error (${currentPlayerData.name}):`, err.message);
    return null;
  }
};

const appendTurnPreconditions = (move, turnPreconditions) => {
  if (!move || !turnPreconditions || typeof turnPreconditions !== 'object') {
    return move;
  }

  const enrichedMove = { ...move };

  if (Number.isInteger(turnPreconditions.expectedCurrentPlayerIndex)) {
    enrichedMove.expectedCurrentPlayerIndex = turnPreconditions.expectedCurrentPlayerIndex;
  }

  if (typeof turnPreconditions.expectedCurrentPlayerName === 'string' && turnPreconditions.expectedCurrentPlayerName.length > 0) {
    enrichedMove.expectedCurrentPlayerName = turnPreconditions.expectedCurrentPlayerName;
  }

  if (typeof turnPreconditions.expectedCurrentPlayerIsAI === 'boolean') {
    enrichedMove.expectedCurrentPlayerIsAI = turnPreconditions.expectedCurrentPlayerIsAI;
  }

  return enrichedMove;
};

export const serializeAIDecision = (decision, turnPreconditions = null) => {
  if (!decision || !decision.action) {
    return null;
  }

  if (decision.action === 'place' && decision.position) {
    return appendTurnPreconditions({
      type: 'place',
      markerIndex: decision.markerIndex,
      row: decision.position.row,
      col: decision.position.col,
    }, turnPreconditions);
  }

  if (decision.action === 'swap') {
    return appendTurnPreconditions({
      type: 'swap',
      targetPlayerIndex: decision.targetPlayerId,
    }, turnPreconditions);
  }

  return null;
};

/**
 * AI Move Executor - to be called from game loop when it's AI's turn
 * @param {Object} gameState - Current game state
 * @param {Function} onMove - Callback to execute the move (receives AI decision)
 * @param {number} delay - Delay in ms (for realism)
 */
/**
 * Execute an AI move with natural delay.
 * Decisions always flow through makeDecision(); when no explicit botProfile is
 * passed, a minimal persona profile is synthesized from the current player state.
 *
 * @param {Object}   gameState  – current game state
 * @param {Function} onMove     – callback with the decision
 * @param {number}   delay      – milliseconds to wait before acting
 * @param {Object}   [botProfile] – { username, rating, bot_persona } for DB bots
 */
export const executeAIMove = async (gameState, onMove, delay = 1000, botProfile = null) => {
  const decision = await planAIMove(gameState, botProfile);
  if (!decision) {
    return;
  }

  // Wait a moment (to feel natural)
  await new Promise(resolve => setTimeout(resolve, delay));

  // Make the move
  return onMove(decision);
};
